import React, { useState, createContext, useContext } from 'react';
type Language = 'en' | 'pl';
type Translations = {
  nav: {
    about: string;
    services: string;
    calendar: string;
    contact: string;
    terms: string;
  };
  hero: {
    title: string;
    subtitle: string;
    cta: string;
  };
  about: {
    title: string;
    story: string;
    philosophy: string;
  };
  services: {
    title: string;
    hair: string;
    nails: string;
    makeup: string;
    spa: string;
    hairDesc: string;
    nailsDesc: string;
    makeupDesc: string;
    spaDesc: string;
  };
  calendar: {
    title: string;
    subtitle: string;
    selectDate: string;
    availableSlots: string;
    bookBtn: string;
    noSlots: string;
  };
  contact: {
    title: string;
    name: string;
    email: string;
    phone: string;
    message: string;
    send: string;
    address: string;
    hours: string;
    contactInfo: string;
  };
  terms: {
    title: string;
    intro: string;
    cancellation: string;
    cancellationText: string;
    privacy: string;
    privacyText: string;
    health: string;
    healthText: string;
  };
  footer: {
    rights: string;
    follow: string;
  };
};
const translations: Record<Language, Translations> = {
  en: {
    nav: {
      about: 'About Us',
      services: 'Services',
      calendar: 'Book Now',
      contact: 'Contact',
      terms: 'Terms'
    },
    hero: {
      title: 'Reveal Your Natural Radiance',
      subtitle: 'A sanctuary of beauty and relaxation in the heart of the city. Experience personalized treatments designed to help you look and feel your absolute best.',
      cta: 'Book an Appointment'
    },
    about: {
      title: 'Our Story',
      story: 'Founded in 2020, Glow Studio began with a simple mission: to create a space where beauty meets wellness. We believe that self-care is not a luxury, but a necessity.',
      philosophy: 'Our philosophy is rooted in natural beauty. We use only premium, eco-friendly products that nourish your body while delivering stunning results. Every treatment is tailored to your unique needs.'
    },
    services: {
      title: 'Our Services',
      hair: 'Hair Styling',
      nails: 'Nail Care',
      makeup: 'Professional Makeup',
      spa: 'Spa Treatments',
      hairDesc: 'From precision cuts to vibrant coloring and restorative treatments.',
      nailsDesc: 'Manicures and pedicures using non-toxic, long-lasting polishes.',
      makeupDesc: 'Event makeup, bridal looks, and personalized tutorials.',
      spaDesc: 'Rejuvenating facials, massages, and body scrubs for total relaxation.'
    },
    calendar: {
      title: 'Book Your Visit',
      subtitle: 'Select a date to view available appointments.',
      selectDate: 'Select a date',
      availableSlots: 'Available Times',
      bookBtn: 'Book Appointment',
      noSlots: 'No slots available for this date.'
    },
    contact: {
      title: 'Get in Touch',
      name: 'Your Name',
      email: 'Email Address',
      phone: 'Phone Number',
      message: 'Message',
      send: 'Send Message',
      address: 'Address',
      hours: 'Opening Hours',
      contactInfo: 'Contact Info'
    },
    terms: {
      title: 'Terms of Service',
      intro: 'Please read our terms carefully before booking.',
      cancellation: 'Cancellation Policy',
      cancellationText: 'We require 24 hours notice for cancellations. Late cancellations may incur a 50% fee.',
      privacy: 'Privacy Policy',
      privacyText: 'We respect your privacy and will never share your personal information with third parties.',
      health: 'Health & Safety',
      healthText: 'Please inform us of any medical conditions or allergies prior to your treatment.'
    },
    footer: {
      rights: 'All rights reserved.',
      follow: 'Follow us'
    }
  },
  pl: {
    nav: {
      about: 'O Nas',
      services: 'Usługi',
      calendar: 'Rezerwacja',
      contact: 'Kontakt',
      terms: 'Regulamin'
    },
    hero: {
      title: 'Odkryj Swój Naturalny Blask',
      subtitle: 'Sanktuarium piękna i relaksu w sercu miasta. Doświadcz spersonalizowanych zabiegów zaprojektowanych, abyś wyglądała i czuła się najlepiej.',
      cta: 'Umów Wizytę'
    },
    about: {
      title: 'Nasza Historia',
      story: 'Założone w 2020 roku, Glow Studio powstało z prostą misją: stworzyć miejsce, gdzie piękno spotyka się z dobrym samopoczuciem. Wierzymy, że dbanie o siebie to nie luksus, ale konieczność.',
      philosophy: 'Nasza filozofia opiera się na naturalnym pięknie. Używamy tylko produktów premium, przyjaznych dla środowiska, które odżywiają Twoje ciało, zapewniając jednocześnie zachwycające rezultaty.'
    },
    services: {
      title: 'Nasze Usługi',
      hair: 'Stylizacja Włosów',
      nails: 'Pielęgnacja Paznokci',
      makeup: 'Makijaż Profesjonalny',
      spa: 'Zabiegi SPA',
      hairDesc: 'Od precyzyjnych cięć po żywą koloryzację i zabiegi regenerujące.',
      nailsDesc: 'Manicure i pedicure przy użyciu nietoksycznych, trwałych lakierów.',
      makeupDesc: 'Makijaż okolicznościowy, ślubny oraz indywidualne lekcje.',
      spaDesc: 'Odmładzające zabiegi na twarz, masaże i peelingi dla pełnego relaksu.'
    },
    calendar: {
      title: 'Zarezerwuj Wizytę',
      subtitle: 'Wybierz datę, aby zobaczyć dostępne terminy.',
      selectDate: 'Wybierz datę',
      availableSlots: 'Dostępne Godziny',
      bookBtn: 'Rezerwuj Wizytę',
      noSlots: 'Brak wolnych terminów w tym dniu.'
    },
    contact: {
      title: 'Skontaktuj się',
      name: 'Twoje Imię',
      email: 'Adres Email',
      phone: 'Numer Telefonu',
      message: 'Wiadomość',
      send: 'Wyślij Wiadomość',
      address: 'Adres',
      hours: 'Godziny Otwarcia',
      contactInfo: 'Dane Kontaktowe'
    },
    terms: {
      title: 'Regulamin',
      intro: 'Prosimy o zapoznanie się z regulaminem przed rezerwacją.',
      cancellation: 'Polityka Anulowania',
      cancellationText: 'Wymagamy powiadomienia o anulowaniu z 24-godzinnym wyprzedzeniem. Późne anulowanie może wiązać się z opłatą 50%.',
      privacy: 'Polityka Prywatności',
      privacyText: 'Szanujemy Twoją prywatność i nigdy nie udostępniamy Twoich danych osobowych osobom trzecim.',
      health: 'Zdrowie i Bezpieczeństwo',
      healthText: 'Prosimy o poinformowanie nas o wszelkich schorzeniach lub alergiach przed zabiegiem.'
    },
    footer: {
      rights: 'Wszelkie prawa zastrzeżone.',
      follow: 'Obserwuj nas'
    }
  }
};
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t: translations[language]
  }} data-id="element-205">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}